import Container from "../../components/element/container";
import { Icon } from "@iconify/react";

import carImage from "../../assets/images/desktop/consign-hero-v2.png";
import teamImage from "../../assets/images/desktop/careers-team.png";
import usePageReveal from "../../components/hook/use.page.reveal";
import "./company.scss";
import "./destinations.scss";

function Action({ children, variant = "outline" }) {
  return (
    <button type="button" className={`company-button is-${variant}`}>
      {children}
      <Icon icon="solar:arrow-right-linear" />
    </button>
  );
}

function Heading({ eyebrow, title, accent, description }) {
  return (
    <header className="company-heading">
      <span className="company-eyebrow">{eyebrow}</span>
      <h1>
        {title}
        <br />
        <em>{accent}</em>
      </h1>
      <p>{description}</p>
    </header>
  );
}

function Blog({ page }) {
  const stories = [
    {
      category: "BUYING GUIDE",
      title: "Finding a car that fits your everyday",
      image: carImage,
      text: "Family space, the daily commute and everything in between. A closer look at building your shortlist.",
    },
    {
      category: "SELLING",
      title: "Your car’s next chapter starts here",
      image: carImage,
      text: "Behind the scenes of consignment, from preparing a listing to meeting its next owner.",
    },
    {
      category: "CAR CULTURE",
      title: "Made for the love of driving",
      image: carImage,
      text: "The design details and small moments that make a car more than a way to get around.",
    },
  ];
  return (
    <>
      <Container tag="div" className="journal-masthead">
        <Heading {...page} />
        <span className="journal-edition">
          THE JOURNAL
          <br />
          <strong>DCAR / STORIES</strong>
        </span>
      </Container>
      <Container tag="div" className="journal-topics" aria-label="Journal topics">
        <span className="active">All stories</span>
        <span>Buying guides</span>
        <span>Selling</span>
        <span>Car culture</span>
        <span>Ownership</span>
      </Container>
      <article className="journal-feature">
        <img src={carImage} alt="Luxury car illuminated in a showroom" />
        <Container tag="div" className="journal-feature-copy">
          <span className="company-eyebrow">EDITOR’S PICK · COMING SOON</span>
          <h2>
            The story behind
            <br />
            your next car.
          </h2>
          <p>
            It starts with a feeling. Explore the choices, conversations and
            details that turn a shortlist into your next chapter.
          </p>
          <Action>Read the story</Action>
        </Container>
      </article>
      <Container tag="div" className="section-heading">
        <h2>On our reading list</h2>
        <span>Stories in the making</span>
      </Container>
      <Container tag="div" className="journal-grid">
        {stories.map((story) => (
          <article className="journal-story" key={story.title}>
            <img src={story.image} alt="" loading="lazy" />
            <Container tag="div">
              <span className="company-eyebrow">{story.category}</span>
              <h3>{story.title}</h3>
              <p>{story.text}</p>
              <Container tag="div" className="story-bottom">
                <span>Coming soon</span>
                <Action variant="text">Read article</Action>
              </Container>
            </Container>
          </article>
        ))}
      </Container>
      <Container tag="div" className="journal-subscribe">
        <Icon icon="solar:letter-linear" />
        <Container tag="div">
          <h2>A little inspiration for the road.</h2>
          <p>
            New stories, perspectives and all things DCAR. Newsletter sign-up is
            coming soon.
          </p>
        </Container>
        <Action variant="solid">Subscribe</Action>
      </Container>
    </>
  );
}

function Insurance({ page }) {
  const coverage = [
    [
      "solar:shield-check-linear",
      "Vehicle protection",
      "Understand the protection described in a motor policy and the situations it covers.",
    ],
    [
      "solar:wind-linear",
      "Windscreen cover",
      "Ask about available windscreen benefits and how they are included in a quotation.",
    ],
    [
      "solar:cloud-rain-linear",
      "Special perils",
      "Discuss optional cover for events such as flood, where offered by the insurer.",
    ],
    [
      "solar:wheel-linear",
      "Roadside support",
      "Check whether assistance is included and how to reach the provider when needed.",
    ],
  ];
  return (
    <>
      <section className="insurance-hero">
        <Container tag="div">
          <Heading {...page} />
          <Container tag="div" className="company-actions">
            <Action variant="solid">Enquire about insurance</Action>
          </Container>
          <p className="availability-note">Online enquiries coming soon</p>
        </Container>
        <Container tag="div" className="insurance-visual">
          <Container tag="div" className="insurance-orbit" aria-hidden="true">
            <Icon icon="solar:shield-check-linear" />
          </Container>
          <span className="insurance-visual-label">MOTOR INSURANCE</span>
          <h2>
            Confidence for
            <br />
            the road ahead.
          </h2>
          <Container tag="div" className="insurance-vehicle">
            <Icon icon="solar:wheel-linear" />
            <span>Your vehicle. Your next journey.</span>
          </Container>
        </Container>
      </section>
      <section className="coverage-section">
        <Container tag="div" className="section-heading">
          <Container tag="div">
            <span className="company-eyebrow">EXPLORE THE DETAILS</span>
            <h2>What would you like to protect?</h2>
          </Container>
          <p>Coverage and optional benefits vary by insurer and policy.</p>
        </Container>
        <Container tag="div" className="coverage-grid">
          {coverage.map(([icon, title, text]) => (
            <article key={title}>
              <Icon icon={icon} />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </Container>
      </section>
      <section className="insurance-enquiry">
        <Container tag="div">
          <span className="company-eyebrow">A SIMPLE START</span>
          <h2>
            Your insurance enquiry,
            <br />
            one step at a time.
          </h2>
          <ol className="numbered-steps">
            <li>
              <strong>Share your vehicle details</strong>
              <p>
                Tell us about your car and whether this is a purchase or
                renewal.
              </p>
            </li>
            <li>
              <strong>Talk through your questions</strong>
              <p>
                Discuss the options and documents you would like help
                understanding.
              </p>
            </li>
            <li>
              <strong>Review your quotation</strong>
              <p>
                Read the provider’s terms, benefits and exclusions before
                deciding.
              </p>
            </li>
          </ol>
        </Container>
        <aside className="enquiry-preview">
          <Icon icon="solar:document-text-linear" />
          <h3>Let’s get the details right.</h3>
          <p>Have these details ready for your enquiry.</p>
          <ul>
            <li>Vehicle make and model</li>
            <li>Registration year and number</li>
            <li>Current policy details, if renewing</li>
            <li>Your preferred contact details</li>
          </ul>
          <Action>Start an enquiry</Action>
          <span className="availability-note">Enquiry form coming soon</span>
        </aside>
      </section>
      <Container tag="div" className="insurance-note">
        <Icon icon="solar:info-circle-linear" />
        <p>
          DCAR assists with insurance enquiries. Cover, premiums and approval
          are determined by the insurer. Available providers and policy details
          will be shared with your quotation.
        </p>
      </Container>
    </>
  );
}

function Careers({ page }) {
  const teams = [
    [
      "01",
      "Customer experience",
      "Sales & customer support",
      "Help buyers and sellers feel supported, from a first enquiry to the next step.",
    ],
    [
      "02",
      "Vehicle operations",
      "Coordination & handover",
      "Bring care and attention to vehicle presentation, viewings and documentation.",
    ],
    [
      "03",
      "Digital & growth",
      "Technology, design & marketing",
      "Build the tools and stories that connect more people with DCAR.",
    ],
  ];
  return (
    <>
      <section className="careers-hero">
        <Container tag="div" className="careers-title">
          <Heading {...page} />
          <Container tag="div" className="company-actions">
            <Action variant="outline">Explore opportunities</Action>
          </Container>
        </Container>
        <Container tag="div" className="careers-photo">
          <img
            src={teamImage}
            alt="Illustrative Malaysian team collaborating in an automotive showroom office"
          />
        </Container>
      </section>
      <section className="careers-culture">
        <Container tag="div">
          <span className="company-eyebrow">LIFE AT DCAR</span>
          <h2>
            Bring more than
            <br />a love of cars.
          </h2>
          <p>
            Bring curiosity, ownership and the drive to make someone’s day a
            little easier.
          </p>
        </Container>
        <Container tag="div" className="culture-values">
          {[
            [
              "solar:users-group-rounded-linear",
              "People come first",
              "Listen well. Communicate clearly. Care about the person behind the enquiry.",
            ],
            [
              "solar:key-linear",
              "Own your part",
              "Follow through on the details that make the whole journey work.",
            ],
            [
              "solar:lightbulb-linear",
              "Keep getting better",
              "Ask questions, share ideas and find a better way together.",
            ],
          ].map(([icon, title, text]) => (
            <article key={title}>
              <Icon icon={icon} />
              <Container tag="div">
                <h3>{title}</h3>
                <p>{text}</p>
              </Container>
            </article>
          ))}
        </Container>
      </section>
      <section className="careers-teams">
        <Container tag="div" className="section-heading">
          <Container tag="div">
            <span className="company-eyebrow">FIND YOUR PLACE</span>
            <h2>Explore our teams</h2>
          </Container>
          <span>Team previews · applications coming soon</span>
        </Container>
        {teams.map(([number, title, area, text]) => (
          <article className="career-row" key={title}>
            <span className="career-number">{number}</span>
            <Container tag="div">
              <h3>{title}</h3>
              <span className="career-area">{area}</span>
              <p>{text}</p>
            </Container>
            <Action variant="solid">View opportunities</Action>
          </article>
        ))}
      </section>
      <Container tag="div" className="careers-invitation">
        <h2>A different kind of drive?</h2>
        <p>
          Specific openings will appear here when available. There may be a
          place for the strengths only you can bring.
        </p>
        <Action>Introduce yourself</Action>
      </Container>
    </>
  );
}

function Dealership({ page }) {
  return (
    <>
      <section className="dealer-hero">
        <Heading {...page} />
        <Container tag="div" className="company-actions">
          <Action variant="solid">Become a partner</Action>
          <span className="availability-note">Applications coming soon</span>
        </Container>
        <Container tag="div"
          className="dealer-network"
          aria-label="Connect your dealership, inventory and customer experience through DCAR"
        >
          <Container tag="div" className="network-node">
            <Icon icon="solar:garage-linear" />
            <strong>Your dealership</strong>
            <span>Experience & inventory</span>
          </Container>
          <Container tag="div" className="network-line" aria-hidden="true" />
          <Container tag="div" className="network-hub">
            DCAR<span>PARTNER NETWORK</span>
          </Container>
          <Container tag="div" className="network-line" aria-hidden="true" />
          <Container tag="div" className="network-node">
            <Icon icon="solar:users-group-rounded-linear" />
            <strong>The customer journey</strong>
            <span>Discovery to handover</span>
          </Container>
        </Container>
      </section>
      <section className="dealer-opportunities">
        <Container tag="div">
          <span className="company-eyebrow">BETTER, TOGETHER</span>
          <h2>
            Your expertise.
            <br />A shared direction.
          </h2>
          <p>
            Explore a partnership built around clear vehicle information,
            thoughtful service and the people buying and selling cars.
          </p>
        </Container>
        <Container tag="div" className="dealer-benefits">
          {[
            [
              "01",
              "Present your inventory",
              "Discuss how your vehicles and listing information could be introduced to the DCAR marketplace.",
            ],
            [
              "02",
              "Connect around enquiries",
              "Explore how buyer conversations, vehicle viewings and follow-ups could work together.",
            ],
            [
              "03",
              "Coordinate the journey",
              "Agree on practical responsibilities, communication and the experience you want to deliver.",
            ],
          ].map(([number, title, text]) => (
            <article key={title}>
              <span>{number}</span>
              <Container tag="div">
                <h3>{title}</h3>
                <p>{text}</p>
              </Container>
            </article>
          ))}
        </Container>
      </section>
      <section className="dealer-onboarding">
        <Container tag="div" className="section-heading">
          <Container tag="div">
            <span className="company-eyebrow">LET’S START A CONVERSATION</span>
            <h2>A path to partnership</h2>
          </Container>
        </Container>
        <Container tag="div" className="dealer-timeline">
          {[
            [
              "Introduce your business",
              "Share your dealership profile, location and vehicle focus.",
            ],
            [
              "Explore the fit",
              "Discuss your goals and how a collaboration could work.",
            ],
            [
              "Agree the next steps",
              "Review the program details and requirements when available.",
            ],
          ].map(([title, text], i) => (
            <article key={title}>
              <span>{i + 1}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </Container>
        <Container tag="div" className="dealer-apply">
          <Container tag="div">
            <h3>Ready to bring your business into the conversation?</h3>
            <p>
              Partner applications and program terms will be available here.
            </p>
          </Container>
          <Action>Register your business</Action>
        </Container>
      </section>
    </>
  );
}

const pages = {
  journal: Blog,
  insurance: Insurance,
  careers: Careers,
  partners: Dealership,
};

export default function EditorialPage({ page }) {
  const Page = pages[page.type];
  const revealRef = usePageReveal(page.type);
  return (
    <section
      className={`company-page destination-page destination-${page.type}`}
      ref={revealRef}
    >
      <Container tag="div" className="company-inner">
        <Page page={page} />
      </Container>
    </section>
  );
}
